import axios from "../axios";
import {
  normalizeEvaluation,
  toApiEvaluationPayload,
  unwrapEvaluationList,
  unwrapEvaluationRecord,
} from "../../components/dashboard/hse/evaluation/evaluationUtils";
import { downloadHsePdfBlob, raiseHsePdfRequestError } from "./downloadPdf";

const EVALUATIONS_URL = "/hse/evaluations";
const TILTAK_URL = "/hse/tiltak";

export function evaluationApiError(error) {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(". ");
  return data?.error || data?.message || error?.message || "";
}

function listParams(filters = {}) {
  const params = {};
  const search = filters.search || filters.query;
  if (search) params.search = search;
  if (filters.status) params.status = filters.status;
  const kind = filters.kind || filters.evaluation_type;
  if (kind) params.kind = kind;
  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;
  return params;
}

export async function fetchEvaluations(filters = {}) {
  const kind = filters.kind || filters.evaluation_type || "";
  const response = await axios.get(EVALUATIONS_URL, { params: listParams(filters) });
  const { items, meta } = unwrapEvaluationList(response.data);
  return {
    data: items.map((item) => normalizeEvaluation(item, kind)).filter(Boolean),
    meta,
  };
}

export async function fetchEvaluation(id, kind = "") {
  const response = await axios.get(`${EVALUATIONS_URL}/${id}`);
  const record = unwrapEvaluationRecord(response.data);
  return normalizeEvaluation(record, kind);
}

export async function createEvaluation(form, kind) {
  const response = await axios.post(EVALUATIONS_URL, {
    evaluation: toApiEvaluationPayload(form, kind),
  });
  return normalizeEvaluation(unwrapEvaluationRecord(response.data), kind);
}

export async function updateEvaluation(id, form, kind) {
  const response = await axios.put(`${EVALUATIONS_URL}/${id}`, {
    evaluation: toApiEvaluationPayload(form, kind),
  });
  return normalizeEvaluation(unwrapEvaluationRecord(response.data), kind);
}

export async function deleteEvaluation(id) {
  await axios.delete(`${EVALUATIONS_URL}/${id}`);
  return true;
}

export async function exportEvaluationPdf(id, filename) {
  try {
    const response = await axios.get(`${EVALUATIONS_URL}/${id}/export_pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    await downloadHsePdfBlob(response.data, filename || `hse-evaluation-${id}.pdf`);
  } catch (error) {
    await raiseHsePdfRequestError(error);
  }
}

export async function fetchEvaluationTiltak(evaluationId) {
  const response = await axios.get(TILTAK_URL, {
    params: { hse_evaluation_id: evaluationId, per_page: 50 },
  });
  const payload = response.data;
  return Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
}

export async function createEvaluationTiltak(evaluationId, action) {
  const allowedTypes = ["generic", "new_routine", "linked_document"];
  const actionType = allowedTypes.includes(action?.action_type)
    ? action.action_type
    : "generic";
  const response = await axios.post(TILTAK_URL, {
    tiltak: {
      title: action.title,
      description: action.description || "",
      action_type: actionType,
      deadline: action.deadline || action.due_date,
      responsible_user_id: action.responsible_user_id,
      hse_evaluation_id: evaluationId,
      source: { type: "hse_evaluation" },
    },
  });
  return unwrapEvaluationRecord(response.data);
}

export async function createFindingAction(evaluationId, _findingId, action) {
  return createEvaluationTiltak(evaluationId, action);
}
